/**
 * APIクライアント（axios設定）
 *
 * バックエンドAPI通信の共通設定
 * - ベースURL設定
 * - 認証ヘッダー自動付与
 * - エラーハンドリング
 * - トークンリフレッシュ
 * - 自動リトライ（接続エラー対策）
 */
import axios from 'axios';
import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import logger from './logger';

// APIのベースURL（同じホストのポート8432を使用）
function getApiUrl(): string {
  // 環境変数が設定されている場合はそれを使用
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // ブラウザの場合、現在のホスト名を使ってAPI URLを構築
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    return `http://${hostname}:8432`;
  }
  return 'http://localhost:8432';
}

const API_URL = getApiUrl();

const TOKEN_KEY = 'ma_checker_token';
const REFRESH_TOKEN_KEY = 'ma_checker_refresh_token';

// リトライ設定
const RETRY_CONFIG = {
  maxRetries: 3,           // 最大リトライ回数
  retryDelay: 1000,        // リトライ間隔（ミリ秒）
  retryableStatuses: [408, 429, 500, 502, 503, 504],  // リトライ対象のHTTPステータス
};

// リトライ可能なエラーかどうかを判定
function isRetryableError(error: AxiosError): boolean {
  // ネットワークエラー（Connection error）
  if (!error.response && error.code !== 'ECONNABORTED') {
    return true;
  }
  // 特定のHTTPステータス
  if (error.response && RETRY_CONFIG.retryableStatuses.includes(error.response.status)) {
    return true;
  }
  // タイムアウト
  if (error.code === 'ECONNABORTED') {
    return true;
  }
  return false;
}

// 遅延関数
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// APIクライアント作成
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true,  // httpOnly Cookie送信を有効化（CORS対応）
});

// リクエストインターセプター: 認証トークン自動付与
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンスインターセプター: エラーハンドリング + 自動リトライ
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    // リトライ処理（Connection error対策）
    if (isRetryableError(error) && originalRequest) {
      const retryCount = originalRequest._retryCount || 0;

      if (retryCount < RETRY_CONFIG.maxRetries) {
        originalRequest._retryCount = retryCount + 1;

        logger.warn(`API retry attempt ${retryCount + 1}/${RETRY_CONFIG.maxRetries}`, {
          url: originalRequest.url,
          error: error.message,
        });

        // 指数バックオフで待機
        const waitTime = RETRY_CONFIG.retryDelay * Math.pow(2, retryCount);
        await delay(waitTime);

        return apiClient(originalRequest);
      }
    }

    // 401エラー時のトークンリフレッシュ
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (refreshToken) {
        try {
          // リフレッシュトークンで新しいアクセストークンを取得
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token } = response.data;
          localStorage.setItem(TOKEN_KEY, access_token);

          // 元のリクエストを再実行
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }
          return apiClient(originalRequest);
        } catch (refreshError) {
          // リフレッシュ失敗時はログアウト状態にする
          logger.warn('Token refresh failed, clearing auth state');
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem('ma_checker_user');
          window.location.href = '/login';
        }
      }
    }

    // エラーメッセージの整形
    const errorMessage = getErrorMessage(error);
    logger.error('API Error', { error: errorMessage, status: error.response?.status });

    return Promise.reject(error);
  }
);

// エラーメッセージ取得
function getErrorMessage(error: AxiosError): string {
  if (error.response?.data) {
    const data = error.response.data as { message?: string; detail?: string };
    return data.message || data.detail || 'エラーが発生しました';
  }
  if (error.request) {
    return 'サーバーに接続できません';
  }
  return error.message || '不明なエラーが発生しました';
}

// エラーレスポンスからメッセージを抽出するユーティリティ
export function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return getErrorMessage(error);
  }
  if (error instanceof Error) {
    return error.message;
  }
  return '不明なエラーが発生しました';
}

export default apiClient;
